/**
 * ForestHub Tracking v2
 * 
 * Kernfunnel-Tracking für den Schnell-Check-Einstieg.
 * 
 * FUNNEL-STUFEN:
 * 1. landing_view         → Seitenaufruf Landing Page
 * 2. schnellcheck_start   → Klick auf "Schnell-Check starten"
 * 3. schnellcheck_eingabe → Freitext eingegeben + analysiert
 * 4. schnellcheck_erkannt → Step 2: Fallbild angezeigt
 * 5. schnellcheck_einschaetzung → Step 3: Einschätzung angezeigt
 * 6. schnellcheck_naechster_schritt → Step 4: Nächster Schritt angezeigt
 * 7. schnellcheck_cta_click → CTA geklickt (registrieren / projekt)
 * 8. registration_start   → Registrierung begonnen
 */

const trackedEvents = new Set();

export const trackEvent = (eventName, props = {}, allowDuplicate = false) => {
  if (typeof window === 'undefined' || !window.plausible) {
    console.debug('[Tracking] Plausible not available');
    return;
  }

  const eventKey = `${eventName}_${JSON.stringify(props)}`;
  if (!allowDuplicate && trackedEvents.has(eventKey)) {
    return;
  }

  const enrichedProps = {
    ...props,
    page_type: getPageType(),
    device_type: getDeviceType(),
  };

  window.plausible(eventName, { props: enrichedProps });
  trackedEvents.add(eventKey);
  setTimeout(() => trackedEvents.delete(eventKey), 1000);
  console.debug('[Tracking]', eventName, enrichedProps);
};

const getPageType = () => {
  const path = window.location.pathname;
  if (path === '/' || path === '') return 'landing';
  if (path.includes('/schnell-check')) return 'schnellcheck';
  if (path.includes('/fallanalyse')) return 'fallanalyse';
  if (path.includes('/anmelden')) return 'login';
  if (path.includes('/registrieren')) return 'register';
  if (path.includes('/dashboard')) return 'dashboard';
  return 'other';
};

const getDeviceType = () => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// ========================================
// KERNFUNNEL: Schnell-Check
// ========================================

/** Stufe 1: Landing Page CTA geklickt */
export const trackHeroCtaClick = (ctaPosition = 'hero_primary') => {
  trackEvent('hero_cta_click', { cta_position: ctaPosition });
};

/** Stufe 2: Schnell-Check gestartet (Eingabe abgeschickt) */
export const trackSchnellcheckEingabe = (source = 'freitext', demoId = null) => {
  trackEvent('schnellcheck_eingabe', { source, demo_id: demoId || 'eigener_text' }, true);
};

/** Stufe 3: Fallbild angezeigt (Step 2) */
export const trackSchnellcheckErkannt = (falltyp) => {
  trackEvent('schnellcheck_erkannt', { falltyp }, true);
};

/** Stufe 4: Einschätzung angezeigt (Step 3) */
export const trackSchnellcheckEinschaetzung = (empfohlener_weg) => {
  trackEvent('schnellcheck_einschaetzung', { empfohlener_weg }, true);
};

/** Stufe 5: Nächster Schritt angezeigt (Step 4) */
export const trackSchnellcheckNaechsterSchritt = (aktion_typ) => {
  trackEvent('schnellcheck_naechster_schritt', { aktion_typ }, true);
};

/** Stufe 6: CTA in Step 4 geklickt */
export const trackSchnellcheckCtaClick = (cta_typ) => {
  trackEvent('schnellcheck_cta_click', { cta_typ });
};

/** Rückfragen beantwortet */
export const trackSchnellcheckRueckfrage = (anzahl_beantwortet) => {
  trackEvent('schnellcheck_rueckfrage', { anzahl_beantwortet }, true);
};

// ========================================
// AUTH
// ========================================

export const trackRegistrationStart = (role) => {
  trackEvent('registration_start', { user_role: role });
};

export const trackLoginAttempt = () => {
  trackEvent('login_attempt');
};

// ========================================
// LEGACY (kept for compatibility)
// ========================================

export const trackDemoModalOpen = () => {};
export const trackDemoRequestSubmit = () => {};
export const trackWebsiteBridgeClick = () => {
  trackEvent('website_bridge_click', { target: 'foresthub.app' });
};
export const trackAppCtaClick = (destination, source = 'unknown') => {
  trackEvent('app_cta_click', { destination, source });
};

// ========================================
// DIENSTLEISTER TRACKING
// ========================================
export const trackDlPageOpen = () => trackEvent('dl_page_open');
export const trackDlPricingView = () => trackEvent('dl_pricing_view');
export const trackDlMapClick = (bundesland) => trackEvent('dl_map_click', { bundesland });
export const trackDlFormStart = () => trackEvent('dl_form_start');
export const trackDlFormLeistung = (leistung) => trackEvent('dl_form_leistung', { leistung });
export const trackDlFormBundesland = (bundesland) => trackEvent('dl_form_bundesland', { bundesland });
export const trackDlFormZertifizierung = (zert) => trackEvent('dl_form_zertifizierung', { zert });
export const trackDlFormSubmit = () => trackEvent('dl_form_submit');
export const trackDlFormSuccess = (profilId) => trackEvent('dl_form_success', { profil_id: profilId });

// ========================================
// PROJEKT-ENTWURF TRACKING
// ========================================
export const trackProjektEntwurf = (entwurfId) => trackEvent('projekt_entwurf_erstellt', { entwurf_id: entwurfId });

// ========================================
// TIER & VORENTSCHEIDUNG TRACKING
// ========================================

/** Vorentscheidung angezeigt (kostenloser Abschluss) */
export const trackVorentscheidungAngezeigt = (falltyp, zielgruppe) =>
  trackEvent('vorentscheidung_angezeigt', { falltyp, zielgruppe }, true);

/** Nutzer springt nach Vorentscheidung ab */
export const trackVorentscheidungAbsprung = (falltyp) =>
  trackEvent('vorentscheidung_absprung', { falltyp });

/** CTA nach Step 3 geklickt */
export const trackStep3CtaClick = (cta_typ, falltyp) =>
  trackEvent('step3_cta_click', { cta_typ, falltyp }, true);

/** Premium-Teaser angezeigt */
export const trackPremiumTeaserAngezeigt = (falltyp, zielgruppe) =>
  trackEvent('premium_teaser_angezeigt', { falltyp, zielgruppe }, true);

/** Premium-CTA geklickt */
export const trackPremiumCtaKlick = (falltyp, zielgruppe) =>
  trackEvent('premium_cta_klick', { falltyp, zielgruppe });

/** Premium Stolperstein geöffnet */
export const trackPremiumStolpersteinGeoeffnet = (kategorie) =>
  trackEvent('premium_stolperstein_geoeffnet', { kategorie }, true);

/** Premium WPW geöffnet */
export const trackPremiumWpwGeoeffnet = (szenario) =>
  trackEvent('premium_wpw_geoeffnet', { szenario }, true);

/** Premium Quellen geöffnet */
export const trackPremiumQuellenGeoeffnet = () =>
  trackEvent('premium_quellen_geoeffnet', {}, true);

/** Premium-Interesse registriert (Gate ohne Payment) */
export const trackPremiumInteresseRegistriert = (falltyp, quelle = 'premium_gate') =>
  trackEvent('premium_interesse_registriert', { falltyp, quelle });

/** Dashboard Entwurf geöffnet */
export const trackDashboardEntwurfGeoeffnet = (entwurfId) =>
  trackEvent('dashboard_entwurf_geoeffnet', { entwurf_id: entwurfId });

/** Dashboard neuer Schnell-Check */
export const trackDashboardNeuerCheck = () =>
  trackEvent('dashboard_neuer_check');

// ========================================
// FUNNEL-EVENTS: waldrechner → foresthub → app
// ========================================

/** Landing Hero-CTA geklickt (neuer Eventname für Cross-Domain-Funnel) */
export const trackAppLandingStartCta = (ctaPosition = 'hero_primary') => {
  trackEvent('app_landing_start_cta_click', { cta_position: ctaPosition });
};

/** Fallart in der Auswahl geklickt */
export const trackAppFallartSelect = (fallart) => {
  trackEvent('app_fallart_select', { fallart });
};

/** Schnell-Check tatsächlich gestartet (Analyse ausgelöst) */
export const trackAppSchnellcheckStart = (fallart = null) => {
  trackEvent('app_schnellcheck_start', { fallart: fallart || 'keine' });
};

/** Schnell-Check abgeschlossen (erste Einordnung/Ergebnisansicht) */
export const trackAppSchnellcheckComplete = (falltyp = 'unbekannt', fallart = null) => {
  trackEvent('app_schnellcheck_complete', { falltyp, fallart: fallart || 'keine' });
};

/** UTM-Parameter beim Einstieg lesen und in sessionStorage speichern */
export const captureEntrySource = () => {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');
  if (utmSource) {
    sessionStorage.setItem('entry_source', utmSource);
    sessionStorage.setItem('entry_medium', utmMedium || '');
    sessionStorage.setItem('entry_campaign', utmCampaign || '');
  }
};

/** Gespeicherte Entry-Source auslesen */
export const getEntrySource = () => ({
  source: sessionStorage.getItem('entry_source') || '',
  medium: sessionStorage.getItem('entry_medium') || '',
  campaign: sessionStorage.getItem('entry_campaign') || '',
});

export default {
  trackEvent,
  trackHeroCtaClick,
  trackSchnellcheckEingabe,
  trackSchnellcheckErkannt,
  trackSchnellcheckEinschaetzung,
  trackSchnellcheckNaechsterSchritt,
  trackSchnellcheckCtaClick,
  trackSchnellcheckRueckfrage,
  trackRegistrationStart,
  trackLoginAttempt,
  trackDemoModalOpen,
  trackDemoRequestSubmit,
  trackWebsiteBridgeClick,
  trackAppCtaClick,
  trackVorentscheidungAngezeigt,
  trackVorentscheidungAbsprung,
  trackStep3CtaClick,
  trackPremiumTeaserAngezeigt,
  trackPremiumCtaKlick,
  trackPremiumStolpersteinGeoeffnet,
  trackPremiumWpwGeoeffnet,
  trackPremiumQuellenGeoeffnet,
  trackPremiumInteresseRegistriert,
  trackDashboardEntwurfGeoeffnet,
  trackDashboardNeuerCheck,
  trackAppLandingStartCta,
  trackAppFallartSelect,
  trackAppSchnellcheckStart,
  trackAppSchnellcheckComplete,
  captureEntrySource,
  getEntrySource,
};
